import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAllUsers,
  useChangePassword,
  useUpdateUser,
  useUploadProfilePic,
  useDeleteProfilePic,
} from "../../../../api/addEmployee/employee";
import { notifyError, notifySuccess } from "../../../../utils/ToastifyProvider";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { data: users = [], isLoading: isProfileLoading } = useGetAllUsers();
  const { mutate: changePassword, isLoading: isChanging } = useChangePassword();
  const { mutate: updateUser, isLoading: isUpdating } = useUpdateUser();
  const { mutate: uploadProfilePic, isLoading: isUploading } =
    useUploadProfilePic();
  const { mutate: deleteProfilePic, isLoading: isDeleting } =
    useDeleteProfilePic();

  const employeeList = Array.isArray(users) ? users : Object.values(users);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  // ✅ Find employee by id
  const profile = useMemo(
    () => employeeList.find((u) => u._id === id),
    [employeeList, id],
  );

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    position: "",
    department: "",
    username: "",
    email: "",
    mobile: "",
    gender: "",
    role: "", // ✅ Added role field
  });

  const [errors, setErrors] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [profilePic, setProfilePic] = useState(null); // ✅ for preview

  // ✅ Load employee data when found
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        position: profile.position || "",
        department: profile.department || "",
        username: profile.username || "",
        email: profile.email || "",
        mobile: profile.mobile || "",
        gender: profile.gender || "",
        role: profile.role || "",
      });
      setProfilePic(
        profile.profilePic ? `${BASE_DOWNLOAD_URL}${profile.profilePic}` : null,
      );
    }
  }, [profile]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username) newErrors.username = "Username is required";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.department) newErrors.department = "Department is required";
    if (!formData.role) newErrors.role = "Role is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateUser = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!id) {
      notifyError("User ID not found");
      return;
    }

    updateUser({ userId: id, data: formData });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      notifyError("New passwords do not match");
      return;
    }

    changePassword(
      {
        userId: id,
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      },
      {
        onSuccess: () => {
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          });
          setIsPasswordModalOpen(false);
          notifySuccess("Password changed successfully");
        },
      },
    );
  };

  // ✅ Handle Profile Pic Upload
  const handleProfilePicChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePic(URL.createObjectURL(file)); // preview first
      uploadProfilePic({ id, file }); // send to API
    }
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "NA";
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  // ✅ Handle Profile Pic Delete
  const handleDeleteProfilePic = () => {
    if (!id) return;
    deleteProfilePic(
      { id, token: localStorage.getItem("token") },
      {
        onSuccess: () => {
          setProfilePic(null);
        },
      },
    );
  };

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg font-medium">Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg font-medium text-red-500">
          Failed to load employee data.
        </p>
      </div>
    );
  }

  return (
    <div className=" flex items-start justify-center px-4 py-10 mb-6">
      <div className="w-full max-w-3xl bg-white border border-gray-300 rounded-lg shadow p-8 max-h-screen overflow-y-auto">
        <h2 className="text-3xl font-bold text-center mb-6 text-black">
          Edit Employee
        </h2>

        {/* ✅ Profile Picture Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative w-32 h-32">
            {profilePic ? (
              <img
                src={profilePic}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-primary"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-blue-400 flex items-center justify-center text-white text-3xl font-bold border-4 ">
                {getInitials(formData.firstName, formData.lastName)}
              </div>
            )}

            {/* Upload button */}
            {!profilePic && (
              <label className="absolute bottom-0 right-0 bg-blue-400 text-white rounded-full p-2 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleProfilePicChange}
                />
                {isUploading ? "⏳" : "✎"}
              </label>
            )}
          </div>

          {/* Remove button */}
          {profilePic && (
            <button
              onClick={() => setIsConfirmModalOpen(true)}
              className="mt-3 text-red-600 text-sm hover:underline disabled:opacity-50"
              disabled={isDeleting}
            >
              {isDeleting ? "Removing..." : "Remove Profile Picture"}
            </button>
          )}

          {isConfirmModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
                <h3 className="text-lg font-bold mb-4 text-center">
                  Confirm Delete
                </h3>
                <p className="text-center mb-6">
                  Are you sure you want to delete your profile picture?
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setIsConfirmModalOpen(false)}
                    className="px-4 py-2 border rounded-md hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteProfilePic();
                      setIsConfirmModalOpen(false);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:opacity-90"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* <p className="mt-2 text-sm text-gray-600">
            Click icon to change or remove profile picture
          </p> */}
        </div>

        {/* Editable Form */}
        <form
          onSubmit={handleUpdateUser}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <EditableInput
            label="Username"
            value={formData.username}
            error={errors.username}
            onChange={(e) => handleInputChange("username", e.target.value)}
            required
          />
          <EditableInput
            label="First Name"
            value={formData.firstName}
            error={errors.firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            required
          />
          <EditableInput
            label="Last Name"
            value={formData.lastName}
            error={errors.lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            required
          />
          <EditableInput
            label="Email"
            value={formData.email}
            error={errors.email}
            type="email"
            onChange={(e) => handleInputChange("email", e.target.value)}
            required
          />
          <EditableInput
            label="Mobile"
            value={formData.mobile}
            onChange={(e) => handleInputChange("mobile", e.target.value)}
          />

          {/* Department Dropdown */}
          <div>
            <label className="block text-sm font-semibold mb-1 flex items-center gap-1">
              Department <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.department}
              onChange={(e) => handleInputChange("department", e.target.value)}
              className={`w-full border rounded-md py-2 px-4 ${
                errors.department ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Department</option>
              <option value="Administration">Administration</option>
              <option value="Information Technology">
                Information Technology
              </option>
              <option value="Accounting & Finance">Accounting & Finance</option>
              <option value="Design & Creative">Design & Creative</option>
              <option value="Human Resources">Human Resources</option>
              <option value="Digital Marketing">Digital Marketing</option>
              <option value="Warehouse & Logistics">
                Warehouse & Logistics
              </option>
              <option value="Customer Success">Customer Success</option>
              <option value="E-Commerce">E-Commerce</option>
            </select>
            {errors.department && (
              <p className="text-red-500 text-sm mt-1">{errors.department}</p>
            )}
          </div>

          <EditableInput
            label="Position"
            value={formData.position}
            onChange={(e) => handleInputChange("position", e.target.value)}
          />

          {/* Gender Dropdown */}
          <div>
            <label className="block text-sm font-semibold mb-1 flex items-center gap-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gender}
              onChange={(e) => handleInputChange("gender", e.target.value)}
              className={`w-full border rounded-md py-2 px-4 ${
                errors.gender ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            {errors.gender && (
              <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
            )}
          </div>

          {/* ✅ Role Dropdown */}
          <div>
            <label className="block text-sm font-semibold mb-1 flex items-center gap-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleInputChange("role", e.target.value)}
              className={`w-full border rounded-md py-2 px-4 ${
                errors.role ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Role</option>
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
            {errors.role && (
              <p className="text-red-500 text-sm mt-1">{errors.role}</p>
            )}
          </div>
        </form>

        <div className="flex justify-between my-8">
          <button
            type="submit"
            onClick={handleUpdateUser}
            className={`bg-primary text-white px-6 py-2 rounded-md hover:opacity-90 transition ${
              isUpdating ? "opacity-60 cursor-not-allowed" : ""
            }`}
            disabled={isUpdating}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Reusable Input Component
const EditableInput = ({
  label,
  value,
  onChange,
  type = "text",
  error,
  required,
}) => (
  <div>
    <label className="block text-sm font-semibold mb-1 flex items-center gap-1">
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value || ""}
      onChange={onChange}
      className={`w-full border rounded-md py-2 px-4 ${
        error ? "border-red-500" : "border-gray-300"
      }`}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

export default EditUser;
